/**
 * ShipmentsList
 *
 * Shipments tab for tracking substrate log shipments to partners.
 * Includes period filtering, partner filtering, summary cards, and CRUD.
 */

import { useState, useMemo } from 'react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import { Truck, PoundSterling, Users, Plus, Trash2, Loader2, Settings, Pencil, FileText, RefreshCw, AlertCircle, CheckCircle, Scale } from 'lucide-react'
import RecordShipmentModal from './RecordShipmentModal'
import ManagePartnersModal from './ManagePartnersModal'

const REFERENCE_CATEGORY_MAP = {
  'PLT-ND': 'transport',
  '20ftR': 'container_rental',
  '20FT-R-DEL': 'container_rental'
}

const CATEGORY_LABELS = {
  container_rental: 'Container Rental',
  electricity: 'Electricity',
  transport: 'Transport',
  other: 'Other'
}

function formatDate(dateStr) {
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-GB', {
    day: 'numeric', month: 'short'
  })
}

export default function ShipmentsList({ hook, periodStart, periodEnd }) {
  const {
    partners, shipments, purchases, overheads, costShares,
    addShipment, updateShipment, deleteShipment, isLoading,
    partnerXeroInvoices, partnerXeroLoading, partnerXeroError,
    fetchPartnerXeroInvoices
  } = hook

  const [showShipmentModal, setShowShipmentModal] = useState(false)
  const [showPartnersModal, setShowPartnersModal] = useState(false)
  const [partnerFilter, setPartnerFilter] = useState('all')
  const [showAllTime] = useState(true)
  const [deleting, setDeleting] = useState(null)
  const [editingId, setEditingId] = useState(null)
  const [editData, setEditData] = useState({})
  const [saving, setSaving] = useState(false)

  const activePartners = useMemo(() => {
    return partners.filter(p => p.is_active)
  }, [partners])

  // Selected partner for Xero panel
  const selectedPartner = useMemo(() => {
    if (partnerFilter === 'all') return null
    return partners.find(p => p.id === partnerFilter) || null
  }, [partnerFilter, partners])

  // Cost-sharing reconciliation
  const reconciliation = useMemo(() => {
    if (!selectedPartner || !partnerXeroInvoices[selectedPartner.id]) return null

    const partnerShares = costShares.filter(cs =>
      cs.partner_id === selectedPartner.id &&
      cs.effective_from <= periodEnd &&
      (!cs.effective_to || cs.effective_to >= periodStart)
    )

    if (partnerShares.length === 0) return null

    const invoices = partnerXeroInvoices[selectedPartner.id] || []
    const refPatterns = selectedPartner.xero_invoice_references || []

    const rows = partnerShares.map(cs => {
      // Total overhead for this category in period
      const categoryOverheads = overheads.filter(o =>
        o.category === cs.overhead_category &&
        o.period_start <= periodEnd &&
        o.period_end >= periodStart
      )
      const totalCost = categoryOverheads.reduce((sum, o) => sum + Number(o.amount || 0), 0)
      const partnerShare = totalCost * Number(cs.share_ratio)

      // Match invoices by reference patterns for this category
      const categoryPatterns = refPatterns.filter(p => REFERENCE_CATEGORY_MAP[p] === cs.overhead_category)
      const matchingInvoices = invoices.filter(inv => {
        if (!inv.reference) return false
        const inPeriod = inv.date >= periodStart + 'T00:00:00' && inv.date <= periodEnd + 'T23:59:59'
        return inPeriod && categoryPatterns.some(p => inv.reference.includes(p))
      })
      const paid = matchingInvoices
        .filter(inv => inv.status === 'PAID')
        .reduce((sum, inv) => sum + Number(inv.amountPaid || 0), 0)

      const outstanding = Math.max(0, partnerShare - paid)

      return {
        category: cs.overhead_category,
        totalCost,
        shareRatio: Number(cs.share_ratio),
        partnerShare,
        paid,
        outstanding,
        status: outstanding <= 0 && partnerShare > 0 ? 'paid' : paid > 0 ? 'partial' : partnerShare > 0 ? 'unpaid' : 'none'
      }
    })

    const totals = rows.reduce((acc, r) => ({
      totalCost: acc.totalCost + r.totalCost,
      partnerShare: acc.partnerShare + r.partnerShare,
      paid: acc.paid + r.paid,
      outstanding: acc.outstanding + r.outstanding
    }), { totalCost: 0, partnerShare: 0, paid: 0, outstanding: 0 })

    return { rows, totals }
  }, [selectedPartner, costShares, overheads, partnerXeroInvoices, periodStart, periodEnd])

  // Filter shipments by period and partner
  const filteredShipments = useMemo(() => {
    return shipments.filter(s => {
      const inPeriod = showAllTime || (s.shipment_date >= periodStart && s.shipment_date <= periodEnd)
      const matchesPartner = partnerFilter === 'all' || s.partner_id === partnerFilter
      return inPeriod && matchesPartner
    })
  }, [shipments, periodStart, periodEnd, partnerFilter, showAllTime])

  // Period shipment costs
  const periodShipmentCosts = useMemo(() => {
    return filteredShipments.reduce((sum, s) => sum + Number(s.shipment_cost || 0), 0)
  }, [filteredShipments])

  const handleAddShipment = async (shipmentData) => {
    return await addShipment(shipmentData)
  }

  const startEditing = (s) => {
    setEditingId(s.id)
    setEditData({
      shipment_date: s.shipment_date,
      log_count: String(s.log_count),
      shipment_cost: String(s.shipment_cost || 0),
      notes: s.notes || ''
    })
  }

  const cancelEditing = () => {
    setEditingId(null)
    setEditData({})
  }

  const saveEdit = async () => {
    setSaving(true)
    try {
      await updateShipment(editingId, {
        shipment_date: editData.shipment_date,
        log_count: parseInt(editData.log_count, 10),
        shipment_cost: parseFloat(editData.shipment_cost) || 0,
        notes: editData.notes.trim() || null
      })
      setEditingId(null)
      setEditData({})
    } catch (err) {
      console.error('Failed to update shipment:', err)
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id) => {
    setDeleting(id)
    try {
      await deleteShipment(id)
    } catch (err) {
      console.error('Failed to delete shipment:', err)
    } finally {
      setDeleting(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Xero Partner Invoices */}
      {selectedPartner?.xero_contact_id && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-base">
                <FileText className="h-5 w-5" />
                Xero Invoices — {selectedPartner.name}
              </CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={() => fetchPartnerXeroInvoices(selectedPartner)}
                disabled={partnerXeroLoading}
              >
                {partnerXeroLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : partnerXeroInvoices[selectedPartner.id]
                    ? <><RefreshCw className="h-4 w-4 mr-2" />Refresh</>
                    : <><FileText className="h-4 w-4 mr-2" />Load Invoices</>
                }
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {partnerXeroError && (
              <div className="flex items-center gap-2 text-sm text-destructive mb-4">
                <AlertCircle className="h-4 w-4" />
                {partnerXeroError}
              </div>
            )}
            {partnerXeroInvoices[selectedPartner.id] ? (
              partnerXeroInvoices[selectedPartner.id].length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-muted-foreground border-b">
                        <th className="text-left py-2 pr-4 font-medium">Reference</th>
                        <th className="text-left py-2 pr-4 font-medium">Date</th>
                        <th className="text-right py-2 pr-4 font-medium">Amount</th>
                        <th className="text-center py-2 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {partnerXeroInvoices[selectedPartner.id].map(inv => (
                        <tr key={inv.invoiceId} className="border-b last:border-0">
                          <td className="py-2 pr-4 font-mono text-xs">
                            {inv.reference || inv.invoiceNumber}
                          </td>
                          <td className="py-2 pr-4">
                            {new Date(inv.date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' })}
                          </td>
                          <td className="text-right py-2 pr-4">£{Number(inv.total).toFixed(2)}</td>
                          <td className="text-center py-2">
                            {inv.status === 'PAID' ? (
                              <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300">
                                Paid
                              </Badge>
                            ) : inv.status === 'AUTHORISED' ? (
                              <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-300">
                                Unpaid
                              </Badge>
                            ) : (
                              <Badge variant="outline">{inv.status}</Badge>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No invoices found for this partner in Xero.
                </p>
              )
            ) : !partnerXeroLoading && (
              <p className="text-sm text-muted-foreground text-center py-4">
                Click "Load Invoices" to fetch invoice data from Xero.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {/* Cost-Sharing Reconciliation */}
      {reconciliation && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base">
              <Scale className="h-5 w-5" />
              Cost Sharing — {selectedPartner?.name}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left py-2 pr-4 font-medium">Category</th>
                    <th className="text-right py-2 pr-4 font-medium">Total Cost</th>
                    <th className="text-right py-2 pr-4 font-medium">Partner Share</th>
                    <th className="text-right py-2 pr-4 font-medium">Paid</th>
                    <th className="text-right py-2 pr-4 font-medium">Outstanding</th>
                    <th className="text-center py-2 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {reconciliation.rows.map(row => (
                    <tr key={row.category} className="border-b last:border-0">
                      <td className="py-2 pr-4 font-medium">{CATEGORY_LABELS[row.category] || row.category}</td>
                      <td className="text-right py-2 pr-4">£{row.totalCost.toFixed(2)}</td>
                      <td className="text-right py-2 pr-4">£{row.partnerShare.toFixed(2)}</td>
                      <td className="text-right py-2 pr-4">£{row.paid.toFixed(2)}</td>
                      <td className="text-right py-2 pr-4 font-medium">£{row.outstanding.toFixed(2)}</td>
                      <td className="text-center py-2">
                        {row.status === 'paid' ? (
                          <CheckCircle className="h-4 w-4 text-green-600 inline" />
                        ) : row.status === 'partial' ? (
                          <AlertCircle className="h-4 w-4 text-amber-500 inline" />
                        ) : row.status === 'unpaid' ? (
                          <AlertCircle className="h-4 w-4 text-red-500 inline" />
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-medium">
                    <td className="py-2 pr-4">Total</td>
                    <td className="text-right py-2 pr-4">£{reconciliation.totals.totalCost.toFixed(2)}</td>
                    <td className="text-right py-2 pr-4">£{reconciliation.totals.partnerShare.toFixed(2)}</td>
                    <td className="text-right py-2 pr-4">£{reconciliation.totals.paid.toFixed(2)}</td>
                    <td className="text-right py-2 pr-4">£{reconciliation.totals.outstanding.toFixed(2)}</td>
                    <td className="text-center py-2">
                      {reconciliation.totals.outstanding <= 0 && reconciliation.totals.partnerShare > 0 ? (
                        <CheckCircle className="h-4 w-4 text-green-600 inline" />
                      ) : reconciliation.totals.paid > 0 ? (
                        <AlertCircle className="h-4 w-4 text-amber-500 inline" />
                      ) : null}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Shipments Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Truck className="h-5 w-5" />
              Shipments
            </CardTitle>
            <div className="flex items-center gap-2">
              {/* Partner Filter */}
              <Select value={partnerFilter} onValueChange={setPartnerFilter}>
                <SelectTrigger className="w-44">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Partners</SelectItem>
                  {activePartners.map(p => (
                    <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button variant="outline" size="sm" onClick={() => setShowPartnersModal(true)}>
                <Settings className="h-4 w-4 mr-2" />
                Partners
              </Button>
              <Button size="sm" onClick={() => setShowShipmentModal(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Record Shipment
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredShipments.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-muted-foreground border-b">
                    <th className="text-left py-2 pr-4 font-medium">Date</th>
                    <th className="text-left py-2 pr-4 font-medium">Partner</th>
                    <th className="text-left py-2 pr-4 font-medium">Source</th>
                    <th className="text-right py-2 pr-4 font-medium">Logs</th>
                    <th className="text-right py-2 pr-4 font-medium">Cost</th>
                    <th className="text-left py-2 pr-4 font-medium">Notes</th>
                    <th className="text-right py-2 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {filteredShipments.map(s => editingId === s.id ? (
                    <tr key={s.id} className="border-b last:border-0 bg-muted/30">
                      <td className="py-2 pr-4">
                        <Input
                          type="date"
                          value={editData.shipment_date}
                          onChange={(e) => setEditData(d => ({ ...d, shipment_date: e.target.value }))}
                          className="h-8 w-32"
                        />
                      </td>
                      <td className="py-2 pr-4 font-medium">{s.substrate_partners?.name || '—'}</td>
                      <td className="py-2 pr-4 font-mono text-muted-foreground">
                        {s.substrate_log_purchases?.purchase_code || '—'}
                      </td>
                      <td className="text-right py-2 pr-4">
                        <Input
                          type="number"
                          min="1"
                          value={editData.log_count}
                          onChange={(e) => setEditData(d => ({ ...d, log_count: e.target.value }))}
                          className="h-8 w-20 text-right"
                        />
                      </td>
                      <td className="text-right py-2 pr-4">
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editData.shipment_cost}
                          onChange={(e) => setEditData(d => ({ ...d, shipment_cost: e.target.value }))}
                          className="h-8 w-24 text-right"
                        />
                      </td>
                      <td className="py-2 pr-4">
                        <Input
                          value={editData.notes}
                          onChange={(e) => setEditData(d => ({ ...d, notes: e.target.value }))}
                          className="h-8"
                          placeholder="Notes..."
                        />
                      </td>
                      <td className="text-right py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button variant="ghost" size="sm" onClick={cancelEditing} disabled={saving}>
                            <span className="text-xs">Cancel</span>
                          </Button>
                          <Button size="sm" onClick={saveEdit} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <span className="text-xs">Save</span>}
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={s.id} className="border-b last:border-0">
                      <td className="py-2 pr-4">{formatDate(s.shipment_date)}</td>
                      <td className="py-2 pr-4 font-medium">{s.substrate_partners?.name || '—'}</td>
                      <td className="py-2 pr-4 font-mono text-muted-foreground">
                        {s.substrate_log_purchases?.purchase_code || '—'}
                      </td>
                      <td className="text-right py-2 pr-4">
                        <Badge variant="outline">{s.log_count}</Badge>
                      </td>
                      <td className="text-right py-2 pr-4">£{Number(s.shipment_cost || 0).toFixed(2)}</td>
                      <td className="py-2 pr-4 text-muted-foreground truncate max-w-32">{s.notes || ''}</td>
                      <td className="text-right py-2">
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => startEditing(s)}
                          >
                            <Pencil className="h-4 w-4 text-muted-foreground" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(s.id)}
                            disabled={deleting === s.id}
                          >
                            {deleting === s.id
                              ? <Loader2 className="h-4 w-4 animate-spin" />
                              : <Trash2 className="h-4 w-4 text-muted-foreground hover:text-destructive" />
                            }
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t font-medium">
                    <td className="py-2 pr-4" colSpan={3}>Total</td>
                    <td className="text-right py-2 pr-4">{filteredShipments.reduce((sum, s) => sum + s.log_count, 0)}</td>
                    <td className="text-right py-2 pr-4">£{periodShipmentCosts.toFixed(2)}</td>
                    <td colSpan={2}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-8">
              No shipments recorded for this period.
            </p>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <RecordShipmentModal
        open={showShipmentModal}
        onOpenChange={setShowShipmentModal}
        onSubmit={handleAddShipment}
        partners={partners}
        purchases={purchases}
        shipments={shipments}
      />
      <ManagePartnersModal
        open={showPartnersModal}
        onOpenChange={setShowPartnersModal}
        hook={hook}
      />
    </div>
  )
}
